/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-syntax */
import {
  autoRunUntilComplete,
} from '@pikpak_web_player/utils/animation'
import { Tween, Easing } from '@tweenjs/tween.js';
import { useEventListener } from '@vueuse/core'
import {
  computed,
  type CSSProperties,
  nextTick,
  onMounted,
  type Ref,
  ref,
  toRaw,
  watch,
} from 'vue'

/* 依赖注入使用的名称 */
export const enum InjectionKey {
  FILE_MANAGER_PROVIDES = 'FILE_MANAGER_PROVIDES',
}

export enum FileManagerEvent {
  FILE_MANAGER_CLOSE = 'FILE_MANAGER_CLOSE',
  FILE_MANAGER_CLOSE_INSIDE = 'FILE_MANAGER_CLOSE_INSIDE',
  FILE_MANAGER_DOWNLOAD = 'FILE_MANAGER_DOWNLOAD',
  FILE_MANAGER_DETAIL = 'FILE_MANAGER_DETAIL',
  FILE_MANAGER_COPY = 'FILE_MANAGER_COPY',
  FILE_MANAGER_MOVE = 'FILE_MANAGER_MOVE',
  FILE_MANAGER_DELETE = 'FILE_MANAGER_DELETE',
  FILE_MANAGER_DID_DELETE = 'FILE_MANAGER_DID_DELETE',
  FILE_MANAGER_MESSAGE = 'FILE_MANAGER_MESSAGE',
  FILE_MANAGER_FULLSCREEN = 'FILE_MANAGER_FULLSCREEN',
  /** @deprecated */
  FILE_MANAGER_FULLSCREEN_STATUS = 'FILE_MANAGER_FULLSCREEN_STATUS',
  FILE_MANAGER_EXIT_FULLSCREEN_BY_ESC = 'FILE_MANAGER_EXIT_FULLSCREEN_BY_ESC',
  FILE_MANAGER_CONTAINER_SIZE = 'FILE_MANAGER_CONTAINER_SIZE',
  FILE_MANAGER_PREVIEW_SIZE = 'FILE_MANAGER_PREVIEW_SIZE',
  IMAGE_VIEWER_DEG_UPDATED = 'IMAGE_VIEWER_DEG_UPDATED',
  COMPONENT_MOUNTED = 'COMPONENT_MOUNTED',
  CONTAINER_OPACITY_CHANGE = 'CONTAINER_OPACITY_CHANGE',
  /** 切换上一个 */
  FILE_MANAGER_PRE = 'FILE_MANAGER_PRE',
  /** 切换下一个 */
  FILE_MANAGER_NEXT = 'FILE_MANAGER_NEXT',
  /** 重新请求当前文件 */
  FILE_MANAGER_RELOAD = 'FILE_MANAGER_RELOAD',
  FILE_MANAGER_CONTENT_MOVE = 'FILE_MANAGER_CONTENT_MOVE',
  REPORT_CLICK = 'REPORT_CLICK',
  WILL_JUMP_TO_WEB = 'WILL_JUMP_TO_WEB',
  OPEN_APP = 'OPEN_APP',
  FILE_MANAGER_COLLECT = 'FILE_MANAGER_COLLECT',
  /** 更新标题成功的回调 */
  FILE_MANAGER_DID_RENAME = 'FILE_MANAGER_DID_RENAME',
  FILE_MANAGER_THUMBNAIL_UPDATE = 'FILE_MANAGER_THUMBNAIL_UPDATE',
}

type EmitFileManagerEventArguments<T extends keyof WindowEventMap> = WindowEventMap[T] extends CustomEvent<infer D>
  ? D extends never ? [T] : [T, D]
  : [T]
export function emitFileManagerEvent<T extends keyof WindowEventMap>(
  ...args: EmitFileManagerEventArguments<T>
) {
  const [type, detail] = args
  const event = new CustomEvent(type, { detail })

  window.dispatchEvent(event)
}

interface ContentItem {
  val: number;
  style: Pick<CSSProperties, 'width' | 'height' | 'visibility' | 'zIndex' | 'opacity'> & {
    offsetX: number;
    left: number;
    top?: number;
  };
  keepAlive?: boolean;
}

const enum AnimateType {
  FADE = 1,
  SLIDE = 2,
}

export function useContent(
  props: { files: PPFile[], isDesktop?: boolean },
  activeIndex: Ref<number>,
  containerWidth: Ref<number>,
  containerHeight: Ref<number>,
  useAnimate: Ref<boolean>,
) {
  const isDesktop = !!props.isDesktop
  const contentList = ref<ContentItem[]>([])
  const lockSize = ref<{ width: number; height: number } | undefined>()
  const currentAnimateType = ref<AnimateType>(isDesktop ? AnimateType.FADE : AnimateType.SLIDE)
  let lastFileId = props.files[activeIndex.value]?.id || ''

  // 轻量级数据结构, 减少组件更新时的 diff 压力
  // TODO: 性能优化
  const dbIndex = computed(() => toRaw(props.files).map(f => ({ id: f.id })))

  const lockSizeStyle = computed<CSSProperties>(() => {
    if (!lockSize.value) return {}

    return {
      width: `${lockSize.value.width}px`,
      height: `${lockSize.value.height}px`,
    }
  })

  const currentFile = computed(() => props.files[activeIndex.value])

  const reset = async () => {
    contentList.value = []
    lastFileId = ''
  }

  const getFileById = (id: string) => {
    let file: undefined | PPFile

    console.time('get file by id, use time:')
    for (let i = 0; i < props.files.length; i += 1) {
      if (props.files[i].id === id) {
        file = props.files[i]
        break
      }
    }
    console.timeEnd('get file by id, use time:')

    return file
  }

  const getFileByIndex = (index: number): PPFile | undefined => props.files[index]

  const getIndexByFileId = (id: string) => dbIndex.value.findIndex(db => db.id === id)

  const getIsLastFile = (id: string) => getIndexByFileId(id) === dbIndex.value.length - 1

  const getIsOnlyFile = () => dbIndex.value.length <= 1

  const getItemStyle = (index: number, currentIndex: number): ContentItem['style'] => {
    const width = containerWidth.value
    return {
      left: (index - currentIndex) * width,
      offsetX: 0,
      visibility: 'visible' as const,
      opacity: '100%',
    } as ContentItem['style']
  }

  const updateContentList = () => {
    if (activeIndex.value < 0) {
      return contentList.value = []
    }
    const newList = []
    let currentIndex = 0
    if (activeIndex.value - 1 >= 0) {
      newList.push(activeIndex.value - 1)
      currentIndex = 1
    }
    newList.push(activeIndex.value)
    if (activeIndex.value + 1 < dbIndex.value.length) {
      newList.push(activeIndex.value + 1)
    }
    contentList.value = newList.map((v, i) => ({
      val: v,
      style: getItemStyle(i, currentIndex),
    }))
  }

  const unLockSize = () => {
    lockSize.value = undefined
    updateContentList()
  }

  const setLockSize = (width: number, height: number, nextVal?: number) => {
    lockSize.value = {
      width,
      height,
    }

    const currentIndex = contentList.value.findIndex(item => item.val === activeIndex.value)
    // 更新 left 信息, 但不更新内容
    contentList.value.forEach((item, i) => {
      Object.assign(item.style, getItemStyle(i, currentIndex))
    })

    // 下一张直接叠在上一张上面
    if (nextVal !== undefined) {
      const nextIndex = nextVal > activeIndex.value ? currentIndex + 1 : currentIndex - 1

      const item = contentList.value[nextIndex]
      if (item) {
        item.val = nextVal
        item.style.left = 0
        item.style.visibility = 'visible'
        item.style.opacity = '100%'
      }
    }

    if (nextVal === activeIndex.value) {
      unLockSize()
    }
  }

  watch(activeIndex, async (newActiveIndex, lastActiveIndex) => {
    const lastFileId_ = lastFileId
    lastFileId = dbIndex.value[newActiveIndex]?.id || ''
    const isSameFile = lastFileId_ === lastFileId

    if (isSameFile || contentList.value.length === 0) {
      // eslint-disable-next-line no-param-reassign
      useAnimate.value = false
      unLockSize()
      return
    }
    // 步骤一: 更换准备要显示的组件, 更新位置状态, 渲染
    if (contentList.value.length && lastActiveIndex !== undefined) {
      const currentIndex = contentList.value.findIndex(item => item.val === lastActiveIndex)

      // 动画期间, 上一个组件先保持激活状态
      if (currentIndex > -1) {
        contentList.value[currentIndex].keepAlive = true
      }

      const nextIndex = newActiveIndex > lastActiveIndex ? currentIndex + 1 : currentIndex - 1

      if (contentList.value[nextIndex]) {
        contentList.value[nextIndex].val = newActiveIndex
        contentList.value[nextIndex].style.visibility = 'visible'
        contentList.value[nextIndex].style.opacity = '100%'

        if (currentAnimateType.value === AnimateType.FADE) {
          contentList.value[nextIndex].style.left = 0
        }
      }

      // 步骤二: 平移动画, 只更新它们的 style.left
      await nextTick()

      if (currentAnimateType.value === AnimateType.FADE) {
        const initialState = { opacity: 100 }
        const tw = new Tween(initialState)
          .to({ opacity: 0 }, 300)
          .easing(Easing.Cubic.InOut)
          .onUpdate(() => {
            contentList.value = contentList.value.map((item, index) => {
              if (index === nextIndex) {
                item.style.opacity = '100%'
              } else {
                item.style.opacity = `${initialState.opacity}%`
              }
              return item
            })
          })
          .onComplete(() => {
            tw.pause()
            useAnimate.value = false
            unLockSize()
          })
          .start()

        autoRunUntilComplete(tw)
      }

      if (currentAnimateType.value === AnimateType.SLIDE) {
        const copyLefts = contentList.value.map(item => item.style.left) as number[]
        const nearItem = contentList.value.find(item => item.style.offsetX !== 0)
        const offsetX = nearItem?.style.offsetX || 0
        const initialState = { offset: 0 }

        const tw = new Tween(initialState)
          .to({ offset: Math.abs((copyLefts[nextIndex] || 0)) - Math.abs(offsetX) }, 300)
          .easing(Easing.Cubic.InOut)
          .onUpdate(() => {
            contentList.value = contentList.value.map((item, index) => {
              if (newActiveIndex > lastActiveIndex) {
                (item.style.left as number) = copyLefts[index] - initialState.offset
              } else {
                (item.style.left as number) = copyLefts[index] + initialState.offset
              }
              return item
            })
          })
          .onComplete(() => {
            tw.pause()
            useAnimate.value = false
            unLockSize()
          })
          .start()

        autoRunUntilComplete(tw)
      }
    }
  })

  watch(containerWidth, () => {
    if (lockSize.value) return

    updateContentList()
  })

  useEventListener(
    FileManagerEvent.FILE_MANAGER_CONTENT_MOVE,
    (ev) => {
      const { detail: { offsetX, animate, dir } } = ev

      const currentIndex = contentList.value.findIndex(item => item.val === activeIndex.value)
      if (animate) {
        const nearItem = contentList.value.find(item => item.style.offsetX !== 0)
        const start = nearItem?.style.offsetX || 0
        const initialState = { left: start }
        const tw = new Tween(initialState)
          .to({ left: offsetX }, 300)
          .easing(Easing.Cubic.InOut)
          .onUpdate(() => {
            contentList.value = contentList.value.map((item, index) => {
              if (currentIndex !== index) {
                item.style.offsetX = initialState.left
              }
              return item
            })
          })
          .onComplete(() => tw.pause())
          .start()

        autoRunUntilComplete(tw)
      } else {
        contentList.value = contentList.value.map((item, index) => {
          if (dir === 1) {
            if (index < currentIndex) {
              item.style.offsetX = offsetX
            }
          } else if (dir === -1) {
            if (index > currentIndex) {
              item.style.offsetX = offsetX
            }
          } else if (currentIndex !== index) {
            item.style.offsetX = offsetX
          }
          return item
        })
      }
    },
  )

  onMounted(() => {
    updateContentList()
  })

  return {
    contentList,
    getItemStyle,
    dbIndex,
    currentFile,
    getFileById,
    getFileByIndex,
    getIndexByFileId,
    getIsLastFile,
    getIsOnlyFile,
    setLockSize,
    unLockSize,
    lockSizeStyle,
    lockSize,
    resetContentState: reset,
  }
}
